// LibraryView.jsx — the Carton Library workspace (FE Integration PR ③).
//
// Browse own + org-shared cartons, upload PDFs, and load a carton into the
// Auto Imposer or the Editor. Owner/admin also get rename, share/unshare, delete.
// PDF-only: no parametric create in this build.
//
// V2 #97: a SUPERUSER's Library is the backend's browse (superuserLibrary.js):
// a remembered view (whose cartons), filters, a server-side search and pages,
// with each card naming its owner and organisation, and the cartons that are
// not theirs muted. A member's is unchanged.

import React from 'react';
import { useTheme } from './theme';
import { useAuth } from './AuthProvider';
import { useToast } from './ToastProvider';
import DiePreview from './DiePreview';
import DuplicateCartonDialog from './DuplicateCartonDialog';
import SuperuserLibraryBar from './SuperuserLibraryBar';
import {
  listCartons, uploadCarton, deleteCarton, patchCarton, promoteCarton, isDraftCarton,
  canManageCarton, cartonDims, LibraryApiError, browseCartons, listOrganisations,
} from './library';
import { duplicateDialogModel, duplicatePartners, isDuplicateRefusal } from './libraryDuplicates';
import { deleteNote } from './savedJobs';
import {
  NO_FILTERS, PAGE_SIZE, cardMarks, duplicateLine, isSuperuser, normaliseView, pageCount, pagerText, sameView,
} from './superuserLibrary';

function Badge({ children, color, bg, P }) {
  return (
    <span style={{
      fontFamily: '"Geist Mono", monospace', fontSize: 8.5, fontWeight: 600, letterSpacing: 0.4,
      textTransform: 'uppercase', color, background: bg, padding: '2px 6px', borderRadius: 4,
    }}>{children}</span>
  );
}

export default function LibraryView({ onLoadCarton, onViewChanged }) {
  const { P, FN, MO, DISPLAY, inp, fmtLen } = useTheme();
  const { user } = useAuth();
  const toast = useToast();

  const [cartons, setCartons] = React.useState(null);   // null = loading
  const [error, setError] = React.useState(null);
  const [q, setQ] = React.useState('');
  const [busyId, setBusyId] = React.useState(null);     // id being mutated/loaded
  const [renaming, setRenaming] = React.useState(null); // { id, value }
  const fileRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  // V2-AC-26: duplicate warnings waiting for the user's choice, shown one at a
  // time. Each is { model, run, doneTitle, failTitle }; `run` saves anyway.
  const [dupQueue, setDupQueue] = React.useState([]);
  const [dupBusy, setDupBusy] = React.useState(false);
  const [highlight, setHighlight] = React.useState(null);   // { id, label } — why this card is lit
  const cardRefs = React.useRef({});
  const highlightTimer = React.useRef(null);

  // V2 #97 — superusers only. The view is the backend's (remembered, recorded);
  // it is SENT only once the user chooses one (pendingView), never on refresh.
  const su = isSuperuser(user);
  const [suView, setSuView] = React.useState(null);
  const [filters, setFilters] = React.useState(NO_FILTERS);
  const [page, setPage] = React.useState(1);
  const [paging, setPaging] = React.useState(null);     // { page, page_size, total }
  const [orgs, setOrgs] = React.useState([]);
  const [sq, setSq] = React.useState('');               // the search as sent (debounced)
  const pendingView = React.useRef(null);
  const [viewNonce, setViewNonce] = React.useState(0);
  const seq = React.useRef(0);

  const refresh = React.useCallback(async () => {
    setError(null);
    try {
      if (su) {
        const mine = ++seq.current;
        const view = pendingView.current;
        pendingView.current = null;
        const body = await browseCartons({ view, filters: { ...filters, q: sq }, page, pageSize: PAGE_SIZE });
        if (view && onViewChanged) onViewChanged();      // the Recent chips follow the view
        if (mine !== seq.current) return;                // a newer request is under way
        setSuView(body.view);
        setPaging({ page: body.page, page_size: body.page_size, total: body.total });
        setCartons(body.items || []);
        return;
      }
      const list = await listCartons({ include: 'drafts' });
      setCartons(list);
    } catch (e) {
      if (e instanceof LibraryApiError && (e.code === 'blob_unconfigured' || e.code === 'db_unconfigured')) {
        setError({ maintenance: true, msg: 'The carton library isn’t configured on the backend yet.' });
      } else {
        setError({ msg: e instanceof LibraryApiError ? e.message : String(e) });
      }
      setCartons([]);
    }
  }, [su, filters, sq, page, onViewChanged]);

  React.useEffect(() => { refresh(); }, [refresh, viewNonce]);
  React.useEffect(() => {
    if (!su) return undefined;
    const t = setTimeout(() => { setSq(q.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [q, su]);
  React.useEffect(() => {
    if (!su) return undefined;
    let live = true;
    listOrganisations().then((list) => { if (live) setOrgs(list); })
      .catch(() => { /* the organisation picker stays empty; the other views work */ });
    return () => { live = false; };
  }, [su]);

  const chooseView = (next) => {
    if (sameView(suView, next)) return;
    pendingView.current = normaliseView(next);
    setPage(1);
    setViewNonce((n) => n + 1);
  };
  const changeFilters = (patch) => { setFilters((f) => ({ ...f, ...patch })); setPage(1); };
  React.useEffect(() => () => { if (highlightTimer.current) clearTimeout(highlightTimer.current); }, []);

  // Bring a carton's card into view and mark it, with the reason, for 10 s
  // (or until it is clicked) — how the duplicate warning, the badge and
  // "already in your library" link to it.
  const showCarton = React.useCallback((id, label = 'Existing carton') => {
    const scroll = () => {
      const el = cardRefs.current[id];
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    if (cardRefs.current[id]) scroll();
    else { setQ(''); setTimeout(scroll, 60); }      // a search may be hiding it
    setHighlight({ id, label });
    if (highlightTimer.current) clearTimeout(highlightTimer.current);
    highlightTimer.current = setTimeout(() => setHighlight(null), 10000);
  }, []);

  const askAboutDuplicate = (prompt) => setDupQueue((q) => [...q, prompt]);
  const nextDuplicate = () => setDupQueue((q) => q.slice(1));
  const cancelDuplicate = React.useCallback(() => {
    if (!dupBusy) setDupQueue((q) => q.slice(1));
  }, [dupBusy]);
  const confirmDuplicate = async () => {
    const p = dupQueue[0];
    if (!p) return;
    setDupBusy(true);
    try {
      await p.run();
      toast.notify({ kind: 'success', title: p.doneTitle, message: p.model.subject || '' });
      await refresh();
    } catch (e) {
      toast.notify({ kind: 'error', title: p.failTitle, message: e instanceof LibraryApiError ? e.message : String(e) });
    } finally {
      setDupBusy(false);
      nextDuplicate();
    }
  };
  const openFromDuplicate = (id) => { nextDuplicate(); showCarton(id); };

  const card = { background: P.surface, border: `0.5px solid ${P.rule}`, borderRadius: 10, padding: 14 };
  const mutedCard = { background: P.bg, border: `0.5px dashed ${P.rule}` };   // #97: not yours
  const ghost = { fontFamily: MO, fontSize: 10.5, padding: '4px 10px', borderRadius: 6, cursor: 'pointer', background: 'transparent', color: P.muted, border: `0.5px solid ${P.rule}` };

  const doUpload = async (files) => {
    const pdfs = Array.from(files || []).filter((f) => /\.pdf$/i.test(f.name));
    if (!pdfs.length) return;
    setUploading(true);
    let added = 0;
    const already = [];   // records handed back as they were: same file, same carton
    const dups = [];      // V2-AC-26: refused as duplicates — asked about one at a time after
    const before = Object.fromEntries((cartons || []).map((c) => [c.id, c]));
    const tally = (rec) => {
      // Idempotent by bytes → key on id. My own DRAFT of the same file comes
      // back promoted into the library (#86): that one was just saved.
      const was = before[rec.id];
      if (was && !(isDraftCarton(was) && !isDraftCarton(rec))) already.push(rec);
      else added += 1;
    };
    const duplicate = (file, e) => dups.push({
      model: duplicateDialogModel(e, { action: 'save', subject: file.name }),
      run: () => uploadCarton({ file, allowDuplicate: true }),
      doneTitle: 'Saved to library', failTitle: `Couldn’t add ${file.name}`,
    });
    for (const file of pdfs) {
      try {
        tally(await uploadCarton({ file }));
      } catch (e) {
        const msg = e instanceof LibraryApiError ? e.message : String(e);
        if (isDuplicateRefusal(e)) {
          duplicate(file, e);
        } else if (e instanceof LibraryApiError && e.status === 502) {
          // storage hiccup — one retry, then report
          try { tally(await uploadCarton({ file })); }
          catch (e2) {
            if (isDuplicateRefusal(e2)) duplicate(file, e2);
            else toast.notify({ kind: 'error', title: `Couldn’t add ${file.name}`, message: msg });
          }
        } else {
          toast.notify({ kind: 'error', title: `Couldn’t add ${file.name}`, message: msg });
        }
      }
    }
    setUploading(false);
    if (added || already.length) {
      toast.notify({
        kind: 'success', title: 'Library updated',
        message: `${added} added${already.length
          ? `, already in your library: ${already.map((r) => r.name).join(', ')}` : ''}.`,
      });
      await refresh();
      if (already.length) showCarton(already[0].id, 'Already in your library');
    }
    dups.forEach(askAboutDuplicate);
  };

  const doDelete = async (c) => {
    setBusyId(c.id);
    try {
      await deleteCarton({ id: c.id });
      setCartons((list) => list.filter((x) => x.id !== c.id));
      setPaging((p) => (p ? { ...p, total: Math.max(0, p.total - 1) } : p));
      // #88: saved jobs that use it still open with it — say so, so a delete
      // never reads as having broken them.
      const note = deleteNote(c);
      toast.notify({ kind: 'success', title: 'Removed from library', message: note ? `${c.name}. ${note}` : c.name });
    } catch (e) {
      const msg = e instanceof LibraryApiError ? e.message : String(e);
      if (e instanceof LibraryApiError && e.status === 403) toast.notify({ kind: 'error', title: 'Not allowed', message: 'Only the owner or an admin can delete this carton.' });
      else toast.notify({ kind: 'error', title: 'Delete failed', message: msg });
    } finally { setBusyId(null); }
  };

  const doPromote = async (c) => {
    // PR ⑧ §4: draft → curated library (lands private; Share is a separate step).
    setBusyId(c.id);
    try {
      const rec = await promoteCarton({ id: c.id });
      setCartons((list) => list.map((x) => (x.id === c.id ? { ...x, ...rec } : x)));
      toast.notify({ kind: 'success', title: 'Saved to library', message: `${c.name} is now in your library.` });
    } catch (e) {
      if (isDuplicateRefusal(e)) {
        askAboutDuplicate({
          model: duplicateDialogModel(e, { action: 'save', subject: c.name }),
          run: () => promoteCarton({ id: c.id, allowDuplicate: true }),
          doneTitle: 'Saved to library', failTitle: 'Couldn’t save to library',
        });
      } else {
        toast.notify({ kind: 'error', title: 'Couldn’t save to library', message: e instanceof LibraryApiError ? e.message : String(e) });
      }
    } finally { setBusyId(null); }
  };

  const doShare = async (c) => {
    const next = c.visibility === 'org' ? 'private' : 'org';
    setBusyId(c.id);
    try {
      const rec = await patchCarton({ id: c.id, visibility: next });
      setCartons((list) => list.map((x) => (x.id === c.id ? { ...x, ...rec } : x)));
      toast.notify({ kind: 'success', title: next === 'org' ? 'Shared with your team' : 'Set to private', message: c.name });
    } catch (e) {
      if (isDuplicateRefusal(e)) {
        askAboutDuplicate({
          model: duplicateDialogModel(e, { action: 'share', subject: c.name }),
          run: () => patchCarton({ id: c.id, visibility: 'org', allowDuplicate: true }),
          doneTitle: 'Shared with your team', failTitle: 'Update failed',
        });
      } else {
        toast.notify({ kind: 'error', title: 'Update failed', message: e instanceof LibraryApiError ? e.message : String(e) });
      }
    } finally { setBusyId(null); }
  };

  const commitRename = async () => {
    const r = renaming; if (!r) return;
    const name = r.value.trim();
    setRenaming(null);
    if (!name || name === r.original) return;
    setBusyId(r.id);
    try {
      const rec = await patchCarton({ id: r.id, name });
      setCartons((list) => list.map((x) => (x.id === r.id ? { ...x, ...rec } : x)));
    } catch (e) {
      toast.notify({ kind: 'error', title: 'Rename failed', message: e instanceof LibraryApiError ? e.message : String(e) });
    } finally { setBusyId(null); }
  };

  const loadOne = async (c) => {
    setBusyId(c.id);
    try {
      // The parent OPENS THE DESTINATION QUESTION; it does not load yet. The
      // Library is the one entry point with no desk of its own, so which desk
      // the carton lands on is the operator's to answer — see
      // LoadDestinationPrompt. A refusal (a parametric carton with no source
      // PDF) still throws from here, and is reported below.
      await onLoadCarton(c);
    } catch (e) {
      toast.notify({ kind: 'error', title: 'Couldn’t load carton', message: e instanceof LibraryApiError ? e.message : String(e) });
    } finally { setBusyId(null); }
  };

  const byId = Object.fromEntries((cartons || []).map((c) => [c.id, c]));
  // A superuser's list arrives searched and filtered; a member's is searched here.
  const filtered = (cartons || []).filter((c) => {
    if (su || !q.trim()) return true;
    const hay = `${c.name} ${c.filename || ''} ${c.geometry?.filename || ''}`.toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  });

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontFamily: MO, fontSize: 9.5, letterSpacing: 1.5, textTransform: 'uppercase', color: P.accent, marginBottom: 4 }}>Saved dies</div>
          <div style={{ fontFamily: DISPLAY, fontSize: 30, color: P.ink, lineHeight: 1 }}>Carton Library</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={su ? 'Search name, owner, organisation…' : 'Search cartons…'}
            style={{ ...inp, width: su ? 240 : 200 }} />
          <button onClick={() => fileRef.current && fileRef.current.click()} disabled={uploading} style={{
            fontFamily: FN, fontSize: 12.5, fontWeight: 500, padding: '8px 14px', borderRadius: 6, border: 'none',
            cursor: uploading ? 'default' : 'pointer', background: P.ink, color: P.sheet, opacity: uploading ? 0.6 : 1,
          }}>{uploading ? 'Uploading…' : '+ Upload PDF'}</button>
          <input ref={fileRef} type="file" accept="application/pdf" multiple style={{ display: 'none' }}
            onChange={(e) => { doUpload(e.target.files); e.target.value = ''; }} />
        </div>
      </div>

      {su && (
        <SuperuserLibraryBar view={suView} orgs={orgs} filters={filters}
          onView={chooseView} onFilters={changeFilters} busy={cartons === null} />
      )}

      {error && (
        <div style={{
          padding: '12px 14px', borderRadius: 8, marginBottom: 16,
          background: error.maintenance ? P.warnSoft : P.dangerSoft,
          border: `0.5px solid ${error.maintenance ? P.warnLine : P.dangerLine}`,
          fontFamily: FN, fontSize: 12, color: P.ink,
        }}>{error.msg}{!error.maintenance && <button onClick={refresh} style={{ ...ghost, marginLeft: 10 }}>Retry</button>}</div>
      )}

      {/* States */}
      {cartons === null ? (
        <div style={{ fontFamily: MO, fontSize: 12, color: P.dim, padding: '40px 0', textAlign: 'center' }}>Loading library…</div>
      ) : filtered.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', padding: 40 }}>
          <div style={{ fontFamily: DISPLAY, fontSize: 22, color: P.muted, marginBottom: 6 }}>
            {su ? 'No cartons here' : cartons.length === 0 ? 'No cartons saved yet' : 'No matches'}
          </div>
          <div style={{ fontFamily: FN, fontSize: 12, color: P.dim, maxWidth: 380, margin: '0 auto', lineHeight: 1.5 }}>
            {su
              ? 'Nothing in this view matches. Try another search, filter or view.'
              : cartons.length === 0
                ? 'Upload carton PDFs here to reuse their detected dies across jobs. Saved cartons also appear under “Load from library” in the + Add carton dialog in Auto Imposer.'
                : 'Try a different search term.'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 }}>
          {filtered.map((c) => {
            const manage = canManageCarton(c, user);
            const dims = cartonDims(c);
            const shared = c.visibility === 'org';
            const busy = busyId === c.id;
            const parametric = c.source_kind === 'parametric' || c.has_source === false;
            const draft = isDraftCarton(c);
            // #97: on a superuser's Library a carton that is not theirs is muted,
            // and someone else's draft says whose it is. A member's cards are as before.
            const marks = su ? cardMarks(c, user) : null;
            // V2-AC-26: same die AND artwork. A superuser's list is paged, so a
            // duplicate on another page is counted rather than dropped (#97).
            const dup = su ? duplicateLine(c, byId) : { shown: duplicatePartners(c, byId), elsewhere: 0 };
            const partners = dup.shown;
            const lit = highlight && highlight.id === c.id;
            return (
              <div key={c.id} ref={(el) => { cardRefs.current[c.id] = el; }}
                onClick={lit ? () => setHighlight(null) : undefined} style={{
                ...card, ...(marks && marks.muted ? mutedCard : {}),
                display: 'flex', flexDirection: 'column', gap: 10, opacity: busy ? 0.6 : 1,
                position: 'relative',
                ...(lit ? {
                  border: `2px solid ${P.accent}`, background: P.accentSoft,
                  boxShadow: `0 0 0 5px ${P.accentLine}, 0 12px 30px rgba(20,16,12,.18)`,
                  animation: 'lib-pulse 0.9s ease-in-out 3',
                } : {}),
              }}>
                {lit && (
                  <div style={{
                    position: 'absolute', top: -11, left: 12, padding: '2px 9px', borderRadius: 10,
                    background: P.accent, color: '#fff', fontFamily: MO, fontSize: 10, letterSpacing: 0.4,
                  }}>{highlight.label}</div>
                )}
                <div style={{ height: 120, borderRadius: 8, background: P.sheet, border: `0.5px solid ${P.ruleSoft}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <DiePreview vertices={c.geometry?.vertices} size={104} />
                </div>

                {renaming?.id === c.id ? (
                  <input autoFocus value={renaming.value}
                    onChange={(e) => setRenaming({ ...renaming, value: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenaming(null); }}
                    onBlur={commitRename} style={{ ...inp }} />
                ) : (
                  <div style={{ fontFamily: MO, fontSize: 12, fontWeight: 500, color: marks && marks.muted ? P.muted : P.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                )}
                {su && (c.owner_name || c.org_name) && (
                  <div style={{ fontFamily: FN, fontSize: 10.5, color: P.dim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: -4 }}>
                    by{' '}
                    <button onClick={() => changeFilters({ owner: c.owner_id, ownerName: c.owner_name || '' })}
                      title="Show only this owner’s cartons" style={{
                        fontFamily: FN, fontSize: 10.5, padding: 0, border: 'none', background: 'none',
                        color: P.muted, cursor: 'pointer', textDecoration: 'underline dotted',
                      }}>{c.owner_name || 'unknown'}</button>
                    {c.org_name ? ` · ${c.org_name}` : ''}
                  </div>
                )}

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, alignItems: 'center' }}>
                  {dims && <Badge P={P} color={P.muted} bg={P.elev}>{fmtLen(dims.w)} × {fmtLen(dims.h)}</Badge>}
                  {draft
                    ? <Badge P={P} color={P.warn} bg={P.warnSoft}>{marks ? marks.draftLabel : 'Draft'}</Badge>
                    : <Badge P={P} color={shared ? P.ok : P.dim} bg={shared ? P.okSoft : P.elev}>{shared ? 'Shared' : 'Private'}</Badge>}
                  {c.geometry?.die_source && <Badge P={P} color={P.dim} bg={P.elev}>{c.geometry.die_source}</Badge>}
                  {partners.length + dup.elsewhere > 0 && <Badge P={P} color={P.warn} bg={P.warnSoft}>Duplicate</Badge>}
                </div>
                {partners.length + dup.elsewhere > 0 && (
                  <div style={{ fontFamily: FN, fontSize: 10.5, color: P.dim, lineHeight: 1.5 }}>
                    Same die and artwork as{' '}
                    {partners.map((d, i) => (
                      <React.Fragment key={d.id}>
                        {i > 0 && ', '}
                        <button onClick={() => showCarton(d.id)} title="Show this carton" style={{
                          fontFamily: FN, fontSize: 10.5, padding: 0, border: 'none', background: 'none',
                          color: P.accent, cursor: 'pointer', textDecoration: 'underline',
                        }}>{d.name}</button>
                      </React.Fragment>
                    ))}
                    {dup.elsewhere > 0 && `${partners.length ? ' and ' : ''}${dup.elsewhere} ${dup.elsewhere === 1 ? 'carton' : 'cartons'} on another page`}
                  </div>
                )}

                <div style={{ display: 'flex', gap: 6, marginTop: 'auto', flexWrap: 'wrap' }}>
                  <button onClick={() => loadOne(c)} disabled={busy}
                    title="Load this carton — you choose whether it goes to the Auto Imposer, the Editor, or both" style={{
                    fontFamily: MO, fontSize: 10.5, padding: '5px 11px', borderRadius: 6, border: 'none',
                    cursor: 'pointer', background: P.accent, color: '#fff', flex: 1,
                  }}>Load →</button>
                  {manage && (
                    <>
                      <button onClick={() => setRenaming({ id: c.id, value: c.name, original: c.name })} disabled={busy} title="Rename" style={ghost}>Rename</button>
                      {draft
                        ? <button onClick={() => doPromote(c)} disabled={busy} title="Keep this carton in your library" style={{ ...ghost, color: P.ok, borderColor: P.okLine }}>Save to library</button>
                        : <button onClick={() => doShare(c)} disabled={busy} title={shared ? 'Make private' : 'Share with your team'} style={ghost}>{shared ? 'Unshare' : 'Share'}</button>}
                      <button onClick={() => doDelete(c)} disabled={busy} title={draft ? 'Discard this draft' : 'Remove from library'} style={{ ...ghost, color: P.danger, borderColor: P.dangerLine }}>{draft ? 'Discard' : 'Delete'}</button>
                    </>
                  )}
                </div>
                {parametric && <div style={{ fontFamily: MO, fontSize: 9, color: P.dim }}>No source PDF — not loadable for export</div>}
              </div>
            );
          })}
        </div>
      )}

      {su && paging && paging.total > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 18 }}>
          <button style={ghost} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>‹ Previous</button>
          <span style={{ fontFamily: MO, fontSize: 11, color: P.muted }}>{pagerText(paging)}</span>
          <button style={ghost} disabled={page >= pageCount(paging)} onClick={() => setPage((p) => p + 1)}>Next ›</button>
        </div>
      )}

      <style>{'@keyframes lib-pulse { 0%, 100% { transform: scale(1); } 45% { transform: scale(1.035); } }'}</style>
      <DuplicateCartonDialog pending={dupQueue[0] || null} busy={dupBusy}
        onConfirm={confirmDuplicate} onCancel={cancelDuplicate} onOpen={openFromDuplicate} />
    </div>
  );
}
